import { forwardRef, ReactNode, TextareaHTMLAttributes, useState } from 'react';

import clsx from 'clsx';

import { textareaElement } from './Textarea.css';
import { TextareaSize } from './types';
import { Colors, sprinkles } from '../../tokens';
import { IconLoadingLine } from '../Icon';
import IconXFill from '../Icon/IconXFill';
import { inputIcon, inputWrapper } from '../Input/Input.css';

export interface TextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  error?: boolean;
  loading?: boolean;
  textareaSize?: TextareaSize;
  inputType?: 'normal' | 'border';
  startIcon?: ReactNode;
  onReset?: () => void;
  wrapperClassName?: string;
  height: number;
  maxHeight?: number;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      error = false,
      loading = false,
      textareaSize = 'm',
      inputType = 'normal',
      disabled = false,
      startIcon,
      onReset,
      wrapperClassName,
      className,
      onFocus,
      onBlur,
      height,
      maxHeight,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false);

    const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    return (
      <div
        className={clsx(
          inputWrapper({
            inputType,
            size: textareaSize,
            error,
            disabled,
            focused: isFocused,
            hasStartIcon: loading || Boolean(startIcon),
            hasEndIcon: Boolean(onReset),
          }),
          wrapperClassName,
        )}
        style={{
          height: 'auto',
          alignItems: 'flex-start',
        }}
      >
        {(loading || startIcon) && (
          <div className={inputIcon({ size: textareaSize })}>
            {loading ? <IconLoadingLine spin /> : startIcon}
          </div>
        )}
        <textarea
          ref={ref}
          className={clsx(textareaElement({ size: textareaSize, disabled }), className)}
          disabled={disabled}
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={{
            minHeight: `${height}px`,
            maxHeight: `${maxHeight ?? height}px`,
            ...props.style,
          }}
          {...props}
        />
        {onReset && (
          <div
            className={clsx(inputIcon({ size: textareaSize }), sprinkles({ cursor: 'pointer' }))}
            onClick={onReset}
          >
            <IconXFill fill={Colors.gray600} />
          </div>
        )}
      </div>
    );
  },
);

Textarea.displayName = 'Textarea';
